<template>
	<div class="file-uploader">
		<!-- 预览：图片缩略图 / 视频播放器 / PDF、HTML 内嵌 -->
		<div v-if="value" class="fu-preview" :class="'is-' + type">
			<img v-if="type === 'image'" :src="value" class="fu-img" alt="" @click="showPreview = true" />
			<video v-else-if="type === 'video'" :src="value" controls preload="metadata" class="fu-video"></video>
			<iframe v-else-if="type === 'pdf' || type === 'html'" :src="value" class="fu-frame" scrolling="yes"></iframe>
			<span v-else class="fu-file">{{ value }}</span>
		</div>

		<!-- 完整路径：独占一行，可直接编辑 -->
		<input
			class="fu-input"
			type="text"
			:value="value"
			:placeholder="placeholder"
			@input="onInput"
			@keydown.enter.prevent
		/>

		<!-- 按钮行：上传 / 新窗口打开 同一行 -->
		<div class="fu-actions">
			<button type="button" class="fu-btn" @click="pick">{{ value ? '重新上传' : '上传' }}</button>
			<a
				v-if="value && (type === 'pdf' || type === 'html')"
				class="fu-open"
				:href="value"
				target="_blank"
				rel="noopener noreferrer"
			>新窗口打开</a>
		</div>

		<!-- 上传进度 -->
		<div v-if="progress" class="fu-progress"><span :style="{ width: progress + '%' }"></span></div>
		<input ref="input" type="file" :accept="accept" @change="onChange" style="display:none" />

		<!-- 图片放大预览(灯箱)：点击任意处关闭 -->
		<div v-if="showPreview && type === 'image' && value" class="fu-lightbox" @click="showPreview = false">
			<img :src="value" class="fu-lightbox-img" alt="" />
		</div>
	</div>
</template>

<script>
// 通用文件上传控件：
// - v-model 绑定文件地址，地址输入框可直接编辑(支持粘贴外链)
// - 也可点上传按钮选文件，走全局 $upload，成功后回填地址
// - 按 type 显示预览：image / video / pdf / html
export default {
	name: 'FileUploader',
	props: {
		// 文件地址
		value: { type: String, default: '' },
		// 允许选择的文件类型：image/* 、video/* 、application/pdf 、.html 等
		accept: { type: String, default: '' },
		// 预览类型：image / video / pdf / html，其它值只显示文本
		type: { type: String, default: '' },
		// 地址输入框占位提示
		placeholder: { type: String, default: '可直接填写或粘贴文件地址' },
	},
	data() {
		return { progress: 0, showPreview: false }
	},
	methods: {
		// 手动编辑地址
		onInput(e) {
			this.$emit('input', e.target.value)
		},
		pick() {
			this.$refs.input.value = ''
			this.$refs.input.click()
		},
		onChange(e) {
			const file = e.target.files && e.target.files[0]
			if (!file) return
			this.progress = 1
			this.$upload(file, p => { this.progress = p }).then(url => {
				this.progress = 0
				this.$emit('input', url)
			}).catch(err => {
				this.progress = 0
				this.$message && this.$message.error((err && err.message) || '上传失败')
			})
		},
	},
}
</script>

<style scoped>
.file-uploader { display: block; max-width: 100%; }

/* 预览容器 */
.fu-preview {
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
	background: #f8fafc;
	line-height: 0;
	margin-bottom: .45rem;
}
/* 图片预览：在预览区内水平居中 */
.fu-preview.is-image { display: flex; align-items: center; justify-content: center; padding: .35rem; }
.fu-preview .fu-img { display: block; margin: 0 auto; max-width: 100%; max-height: 120px; object-fit: contain; cursor: zoom-in; }
.fu-preview .fu-video { display: block; width: 100%; max-height: 180px; background: #000; }
/* iframe：视口给足高度(模板多为整屏设计，太矮会被压扁/只露一点)，并允许内部滚动 */
.fu-preview .fu-frame {
	display: block;
	width: 100%;
	height: 520px;
	border: 0;
	background: #fff;
	-webkit-overflow-scrolling: touch;
}
.fu-file { display: inline-block; padding: .5rem .8rem; font-size: .85rem; color: #2563eb; line-height: 1.4; word-break: break-all; }

/* 完整路径输入框：整行铺满(预览、输入框、按钮行 自上而下排列) */
.fu-input {
	display: block;
	width: 100%;
	box-sizing: border-box;
	margin-bottom: .45rem;
	padding: .4rem .6rem;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	font-size: .82rem;
	color: #334155;
	outline: none;
	background: #fff;
}
.fu-input:focus { border-color: #2563eb; }
/* 按钮行：上传 / 新窗口打开 排在同一行，两者之间留出间隙 */
.fu-actions { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
.fu-actions .fu-open { margin-left: .25rem; }
.fu-btn {
	flex-shrink: 0;
	border: 1px solid #2563eb;
	background: #fff;
	color: #2563eb;
	padding: .38rem .9rem;
	border-radius: 6px;
	font-size: .82rem;
	cursor: pointer;
	transition: all .2s ease;
}
.fu-btn:hover { background: #2563eb; color: #fff; }
.fu-open { flex-shrink: 0; font-size: .78rem; color: #2563eb; text-decoration: none; }
.fu-open:hover { text-decoration: underline; }

/* 上传进度 */
.fu-progress { margin-top: .35rem; height: 4px; border-radius: 2px; background: #e2e8f0; overflow: hidden; }
.fu-progress span { display: block; height: 100%; background: #2563eb; transition: width .2s; }

/* 手机端：预览高度跟随视口，接近"一整屏"，模板类页面才能正常展示 */
@media (max-width: 768px) {
	.fu-preview .fu-frame { height: 62vh; }
}

/* 图片放大预览(灯箱)：压在弹窗之上 */
.fu-lightbox {
	position: fixed;
	left: 0; right: 0; top: 0; bottom: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px;
	background: rgba(0, 0, 0, .8);
	cursor: zoom-out;
}
.fu-lightbox-img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
	border-radius: 6px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, .5);
}
</style>
